import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useWindowSize } from "../../../../../Helpers";
import Button from "@components/Button";
import LinearGradientProgressBar from "@components/LinearGradientProgressBar";
import StickyButton from "../../../../../components/StickyButton";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import BrandLogo from "@components/BrandLogo";
import "./style.scss";
import NiceModal from "@ebay/nice-modal-react";
import DOMPurify from "dompurify";

function useInterval(callback, delay) {
  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export default function MemoryMatch({
  currentQuestion,
  setCurrentQuestion,
  sendAnswers,
  answers,
}) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);
  const XGA = width >= 1020;

  const questions = gameQuestionsQuery?.content;
  const answersTiles = gameQuestionsQuery?.content[currentQuestion]?.answers;
  const timeDuration = gameQuestionsQuery?.timer || 60;
  const showTime = gameQuestionsQuery?.timer?.showTimer || true;
  const imagesFullWidth = gameQuestionsQuery?.imagesFullWidth || false;

  const initialBoard = answersTiles
    .map(() => ["", ""])
    .slice(0, answersTiles.length / 2);
  const [board, setBoard] = useState([...initialBoard]);

  const [firstClick, setFirstClick] = useState({});
  const [secondClick, setSecondClick] = useState({});
  const [disableTiles, setDisableTiles] = useState(false);

  const [timer, setTimer] = useState(timeDuration);
  const [timerStarted, setTimerStarted] = useState(false);

  const isUltraInsider =
    location.pathname.includes("/ultra-insider") ||
    location.pathname.includes("/ultrainsider");

  const progress = board.reduce((acc, value) => {
    if (value.some((e) => e == "")) {
      return acc;
    }
    return ++acc;
  }, 0);
  const pairsCount = questions[currentQuestion].answers.filter(
    (e) => e.innerIndex == 0,
  ).length;
  const answersRef = useRef([]);

  function reset() {
    setBoard(initialBoard);
    if (showTime) {
      setTimer(timeDuration);
      setTimerStarted(false);
    }
  }

  function handleNextButtonClick() {
    answersRef.current.push({
      question_ident:
        questions[currentQuestion]?.ident ?? `q${currentQuestion}`,
      answer_ident: board,
    });

    const isLastQuestion =
      currentQuestion + 1 >= gameQuestionsQuery.content.length;

    if (isLastQuestion) {
      sendAnswers(false, [...(answers || []), ...answersRef.current]);
    }

    reset();
    setCurrentQuestion((prev) => ++prev);
  }

  function handleTileClick(pair, innerIndex, uuid) {
    if (disableTiles) {
      return;
    }
    if (Object.keys(firstClick).length !== 0 && firstClick.uuid !== uuid) {
      setSecondClick({ pair, innerIndex, uuid });
      setDisableTiles(true);
      return;
    }
    setFirstClick({ pair, innerIndex, uuid });

    if (!timerStarted && showTime) {
      setTimerStarted(true);
    }
  }

  const checkTiles = () => {
    if (
      Object.keys(firstClick).length !== 0 &&
      Object.keys(secondClick).length !== 0
    ) {
      if (firstClick.pair === secondClick.pair) {
        board[firstClick.pair][firstClick.innerIndex] = firstClick.uuid;
        board[secondClick.pair][secondClick.innerIndex] = secondClick.uuid;
        setDisableTiles(false);
        setFirstClick({});
        setSecondClick({});
      } else {
        setTimeout(() => {
          setFirstClick({});
          setSecondClick({});
          setDisableTiles(false);
        }, 500);
      }
    }
  };

  useInterval(() => {
    if (timer > 0 && timerStarted && board.flat().includes("")) {
      setTimer((prev) => prev - 1);
    }
  }, 1000);

  useEffect(() => {
    if (timer === 0) {
      NiceModal.show("memory-match-modal", { onReset: reset });
    }
  }, [timer]);

  checkTiles();

  console.log(board);

  return (
    <section
      className={`game-page memory-match ${ident} ${
        isUltraInsider ? "ultra-insider" : ""
      }`}
    >
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          ></p>
        </PagesHeader>
        <div className="main-game-wrppaer">
          <div className="desktop-wrapper">
            <LinearGradientProgressBar value={progress / pairsCount} />
            <div className="game-question">
              <h2
                className="question"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(gameQuestionsQuery.content[currentQuestion].question),
                }}
              />
              {timerStarted && timer > 0 && (
                <span className="timer">
                  0:{String(timer).padStart(2, "0")}
                </span>
              )}
            </div>
            <div
              className={`board ${imagesFullWidth ? "full-width-tile" : ""}`}
            >
              {questions[currentQuestion].answers.map((element, index) => (
                <button
                  key={index}
                  className={`tile ${
                    firstClick.uuid === element.uuid ||
                    secondClick.uuid === element.uuid ||
                    board[element.pair][element.innerIndex] !== ""
                      ? "is-flipped"
                      : ""
                  }`}
                  onClick={() =>
                    handleTileClick(
                      element.pair,
                      element.innerIndex,
                      element.uuid,
                    )
                  }
                >
                  <div className="front">
                    <img src={questions[currentQuestion].tile} alt="" />
                  </div>
                  <div className="back">
                    <img
                      src={questions[currentQuestion].answers[index].img}
                      alt=""
                      className="back"
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="side-img">
            <BrandLogo brands={gameQuestionsQuery?.brands} />
            <img
              src={
                gameQuestionsQuery[!XGA ? "sideImgMobile" : "sideImgDesktop"]
              }
              alt=""
            />
            {XGA && (
              <StickyContent>
                <div className="desktop-buttons">
                  <Button
                    disabled={!board.flat().every((e) => !!e)}
                    action={handleNextButtonClick}
                  >
                    {gameQuestionsQuery.content[currentQuestion]
                      .nextButtonCopy ?? "Nächste"}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
        </div>
      </div>
      {!XGA && (
        <StickyButton
          disabled={!board.flat().every((e) => !!e)}
          action={handleNextButtonClick}
        >
          {gameQuestionsQuery.content[currentQuestion].nextButtonCopy ??
            "Nächste"}
        </StickyButton>
      )}
    </section>
  );
}
